import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

const CHANNEL_NAME = "coval-dashboard-deleted"

interface DashboardDeletedEvent {
  dashboardId: string
  organizationSlug: string
}

let publisher: BroadcastChannel | null = null

// Returns a function that tells other tabs a dashboard was deleted, so their
// open pages drop saves and cached data for it. The organization is read from
// this tab's URL when the publisher is created, so call it before starting the
// delete: an organization switch while the request is in flight can't relabel
// the event. Tabs on a different organization ignore it. The channel stays
// open: closing it right after posting can drop the message before receivers
// get it.
export function createDashboardDeletedPublisher(): (
  dashboardId: string,
) => void {
  const organizationSlug =
    typeof window === "undefined"
      ? null
      : getRouteOrganizationSlug(window.location.pathname)
  return (dashboardId) => {
    if (typeof BroadcastChannel === "undefined" || !organizationSlug) {
      return
    }
    publisher ??= new BroadcastChannel(CHANNEL_NAME)
    publisher.postMessage({ dashboardId, organizationSlug })
  }
}

function isDashboardDeletedEvent(data: unknown): data is DashboardDeletedEvent {
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as DashboardDeletedEvent).dashboardId === "string" &&
    typeof (data as DashboardDeletedEvent).organizationSlug === "string"
  )
}

export function subscribeToDashboardDeleted(
  onDeleted: (event: DashboardDeletedEvent) => void,
): () => void {
  if (typeof BroadcastChannel === "undefined") {
    return () => {}
  }
  const channel = new BroadcastChannel(CHANNEL_NAME)
  channel.onmessage = (event: MessageEvent<unknown>) => {
    if (isDashboardDeletedEvent(event.data)) {
      onDeleted(event.data)
    }
  }
  return () => channel.close()
}
